/**
 * Physics World — Static Mode.
 * Places elements at their configured positions WITHOUT any physics,
 * dragging, or collision. No Matter.js required.
 * Used when "Enable Physics" is toggled off in the section settings.
 */
(function () {
    'use strict';

    // ─── Helpers ────────────────────────────────────────────────
    function isMobile() { return window.innerWidth < 768; }

    /**
     * Get true document-level top by walking the offsetParent chain.
     * Temporarily neutralises sticky positioning on ancestors so that
     * offsetTop reflects the original document-flow position.
     */
    function getAbsoluteTop(el) {
        var stickies = [];
        var walker = el;
        while (walker) {
            var cs = window.getComputedStyle(walker);
            if (cs.position === 'sticky') {
                stickies.push({ node: walker, orig: walker.style.position });
                walker.style.position = 'relative';
            }
            walker = walker.parentElement;
        }

        var top = 0;
        var current = el;
        while (current) {
            top += current.offsetTop;
            current = current.offsetParent;
        }

        stickies.forEach(function (s) { s.node.style.position = s.orig; });
        return top;
    }

    /**
     * Resolve reference section from DOM, same logic as physics-world.js
     */
    function resolveRefEl(dataScript, refMode, refSelector) {
        var sectionWrapper = dataScript.closest('.shopify-section');
        var refEl = null;
        if (refMode === 'prev' && sectionWrapper) {
            refEl = sectionWrapper.previousElementSibling;
        } else if (refMode === 'next' && sectionWrapper) {
            refEl = sectionWrapper.nextElementSibling;
        } else if (refMode === 'custom' && refSelector) {
            refEl = document.querySelector(refSelector);
        }
        if (!refEl) refEl = (sectionWrapper && sectionWrapper.previousElementSibling) || sectionWrapper;
        return refEl;
    }

    // ─── Build Fallback SVG (same as physics-world.js) ─────────
    function buildFallbackSVG(div, shape, color) {
        var ns = 'http://www.w3.org/2000/svg';
        var svg = document.createElementNS(ns, 'svg');
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', '100%');
        svg.setAttribute('viewBox', '0 0 100 100');
        svg.style.overflow = 'visible';
        svg.style.pointerEvents = 'none';
        var s;
        if (shape === 'circle') {
            s = document.createElementNS(ns, 'circle');
            s.setAttribute('cx', '50'); s.setAttribute('cy', '50');
            s.setAttribute('r', '48'); s.setAttribute('fill', color);
        } else if (shape === 'pill') {
            s = document.createElementNS(ns, 'rect');
            s.setAttribute('x', '0'); s.setAttribute('y', '15');
            s.setAttribute('width', '140'); s.setAttribute('height', '70');
            s.setAttribute('rx', '35'); s.setAttribute('fill', color);
        } else {
            s = document.createElementNS(ns, 'rect');
            s.setAttribute('x', '2'); s.setAttribute('y', '2');
            s.setAttribute('width', '96'); s.setAttribute('height', '96');
            s.setAttribute('rx', '12'); s.setAttribute('fill', color);
        }
        svg.appendChild(s);
        div.appendChild(svg);
    }

    // ─── State ──────────────────────────────────────────────────
    var overlay = null;
    var placements = []; // { div, meta, dataScript }

    // ─── Create Overlay ─────────────────────────────────────────
    function createOverlay() {
        if (overlay) return overlay;
        // Reuse the physics overlay if it already exists
        var existing = document.getElementById('physics-overlay');
        if (existing) { overlay = existing; return overlay; }

        var el = document.createElement('div');
        el.id = 'physics-overlay-static';
        el.style.cssText =
            'position:absolute;top:0;left:0;width:100%;' +
            'pointer-events:none;z-index:50;overflow:visible;';
        document.body.appendChild(el);
        overlay = el;
        updateOverlayHeight();
        return overlay;
    }

    function updateOverlayHeight() {
        if (!overlay) return;
        var h = Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight
        );
        overlay.style.height = h + 'px';
    }

    // ─── Place Elements ─────────────────────────────────────────
    function placeElements() {
        var scripts = document.querySelectorAll('script[id^="physics-world-static-"]');
        if (!scripts.length) return;

        createOverlay();

        var mobile = isMobile();
        var vpWidth = window.innerWidth;

        scripts.forEach(function (dataScript) {
            var parsed;
            try { parsed = JSON.parse(dataScript.textContent); }
            catch (e) { console.error('Physics World Static: bad JSON', e); return; }

            var elements = parsed.elements || [];
            if (!elements.length) return;

            elements.forEach(function (el) {
                // Don't recreate if already placed
                if (document.getElementById('pw-static-' + el.id)) return;

                var size = mobile ? (el.sizeMobile || el.size) : el.size;
                var posX = mobile ? (el.posXMobile != null ? el.posXMobile : el.posX) : el.posX;
                var posY = mobile ? (el.posYMobile != null ? el.posYMobile : el.posY) : el.posY;
                var refMode = el.refSection || 'prev';

                var refEl = resolveRefEl(dataScript, refMode, el.refSelector);
                var refTop = refEl ? getAbsoluteTop(refEl) : 0;
                var refHeight = refEl ? refEl.offsetHeight : window.innerHeight;

                var xPx = (posX / 100) * vpWidth;
                var yPx = refTop + (posY / 100) * refHeight;

                var shape = el.shape || 'circle';
                var h = shape === 'pill' ? size * 0.8 : size;
                var w = shape === 'pill' ? size * 1.5 : size;

                var div = document.createElement('div');
                div.id = 'pw-static-' + el.id;
                div.className = 'pw-element pw-element--static';
                div.style.cssText =
                    'position:absolute;left:0;top:0;' +
                    'width:' + w + 'px;height:' + h + 'px;' +
                    'pointer-events:none;user-select:none;' +
                    'will-change:transform;';

                // Rotation
                var rotation = el.rotation || 0;
                var rotRad = (rotation * Math.PI) / 180;

                // Position via transform (same as the physics render loop)
                div.style.transform =
                    'translate3d(' + (xPx - w / 2) + 'px,' +
                    (yPx - h / 2) + 'px,0) rotate(' +
                    rotRad + 'rad)';

                if (el.imageUrl) {
                    var img = document.createElement('img');
                    img.src = el.imageUrl;
                    img.alt = el.label || 'Decorative element';
                    img.draggable = false;
                    img.style.cssText =
                        'width:100%;height:100%;object-fit:contain;' +
                        'pointer-events:none;user-select:none;';
                    div.appendChild(img);
                } else {
                    buildFallbackSVG(div, shape, el.color || '#FF5733');
                }

                overlay.appendChild(div);
                placements.push({
                    div: div,
                    dataScript: dataScript,
                    meta: { refMode: refMode, refSelector: el.refSelector || '', posX: posX, posY: posY },
                    size: size,
                    shape: shape,
                    rotation: rotation
                });
            });
        });

        updateOverlayHeight();
    }

    // ─── Reposition (on resize or layout shift) ──────────────────
    function repositionAll() {
        var mobile = isMobile();
        var vpWidth = window.innerWidth;

        placements.forEach(function (p) {
            var refEl = resolveRefEl(p.dataScript, p.meta.refMode, p.meta.refSelector);
            var refTop = refEl ? getAbsoluteTop(refEl) : 0;
            var refHeight = refEl ? refEl.offsetHeight : window.innerHeight;

            var size = mobile ? p.size : p.size; // already resolved at creation
            var shape = p.shape;
            var h = shape === 'pill' ? size * 0.8 : size;
            var w = shape === 'pill' ? size * 1.5 : size;

            var xPx = (p.meta.posX / 100) * vpWidth;
            var yPx = refTop + (p.meta.posY / 100) * refHeight;
            var rotRad = (p.rotation * Math.PI) / 180;

            p.div.style.transform =
                'translate3d(' + (xPx - w / 2) + 'px,' +
                (yPx - h / 2) + 'px,0) rotate(' +
                rotRad + 'rad)';
        });

        updateOverlayHeight();
    }

    // ─── Init ───────────────────────────────────────────────────
    function init() {
        placeElements();

        // Reposition on resize
        var resizeTimer;
        window.addEventListener('resize', function () {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(repositionAll, 200);
        });

        // Reposition when layout changes (images loading, sections expanding)
        var ro = new ResizeObserver(function () {
            repositionAll();
        });
        ro.observe(document.body);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Re-init after full load to catch image-driven layout shifts
    window.addEventListener('load', function () {
        setTimeout(repositionAll, 100);
    });

    // ─── Theme Editor Support ───────────────────────────────────
    if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', function () {
            // Clear existing static elements
            placements.forEach(function (p) { p.div.remove(); });
            placements = [];
            if (overlay) { overlay.remove(); overlay = null; }
            init();
        });
    }
})();
